import React, {
  Children,
  cloneElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import debounce from 'lodash.debounce';
import useClickOutside from 'next/hooks/use-click-outside';
import useIsMounted from 'next/hooks/use-is-mounted';
import useEscape from 'next/hooks/use-escape';

/**
 * HEY LISTEN! Portal needs to be imported via the full path so that
 * Jest can pick up the automock!
 */
// eslint-disable-next-line no-restricted-imports
import Portal from 'next/components/ui/lib/Portal';
import Link from './Link';

import Icon from './Icon';

/**
 * NOTE: This component and the hook `usePosition` borrows heavily from the
 *       `Tether` implementation in the builder. However, the logic has been
 *       simplified greatly to only handle tethering to bottom right or left
 *       corner of the target and does not try to reposition if overflowing off
 *       screen. If we decide later that we need more robust tethering in
 *       studio, we can bring over the full implementation and refactor this
 *       component to leverage that abstraction. To note, `Tooltip` includes a
 *       nearly identical hook as well.
 */

/**
 * Safe helper to call `getBoundingClientRect` with fallback to zeros
 *
 * @param {?Element} $element - Element to get bounds
 * @return {DOMRect} Element's rect or zeros
 */
const getBoundingClientRect = $element =>
  $element
    ? $element.getBoundingClientRect()
    : { top: 0, right: 0, bottom: 0, left: 0, height: 0, width: 0 };

/**
 * Calculate all possible positions for attachment
 *
 * @param {object} params
 * @param {object} params.width - Attachment width
 * @param {DOMRect} rect - Target rect
 * @return {object} All possible top/left attachment positions
 */
const getPositions = ({ width, rect }) => ({
  right: { top: rect.bottom, left: rect.right - width },
  left: { top: rect.bottom, left: rect.left },
});

/**
 * Hook to determine the absolute coordinates for tethering attachment to bottom
 * left or bottom right of the target
 *
 * @param {object} params
 * @param {('left'|'right')} params.placement - Placement of attachment
 * @param {boolean} params.visible - Whether the attachment is visible
 * @return {[object<React.RefObject>, object]} result - Refs and coordinates
 */
const usePosition = ({ placement, visible }) => {
  const target = useRef();
  const attachment = useRef();

  const [position, setPosition] = useState({});

  // Determine the top/left coordinates for tethered attachment based on the
  // position of the target
  const reposition = useCallback(() => {
    const { current: $target } = target;
    const { current: $attachment } = attachment;

    const rect = getBoundingClientRect($target);
    const { width } = getBoundingClientRect($attachment);
    const positions = getPositions({ width, rect });

    setPosition(positions[placement]);
  }, [placement]);

  // Bind scroll and resize event listeners to trigger repositioning
  useLayoutEffect(() => {
    // Skip binding handlers if the attachment is not visible
    if (!visible) {
      return;
    }

    // Create new debounced reposition handler
    // 16ms ~ 60fps
    const handler = debounce(reposition, 16, { leading: true });

    // Immediately reposition on mount and force attachment to render
    reposition();

    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler);
    };
  }, [reposition, visible]);

  return [{ attachment, target }, position];
};

/**
 * Base Dropdown component
 */

const Target = styled.div.attrs({ role: 'button' })`
  cursor: pointer;
`;

const Attachment = styled.div`
  background: var(--white);
  border-radius: 6px;
  box-shadow: var(--dropdown-menu-shadow);
  color: var(--dropdown-text);
  margin-top: 6px;
  padding: 8px;
  position: absolute;
`;

function Dropdown({
  attachment,
  children,
  disabled = false,
  closeOnEscape = true,
  placement = 'right',
  open = false,
  onShow = () => {},
  onHide = () => {},
  ...props
}) {
  const isMounted = useIsMounted();
  const [visible, setIsVisible] = useState(!disabled && open);

  const toggleVisible = () => {
    if (!disabled) {
      setIsVisible(current => !current);
    }
  };

  // Get refs and attachment position
  const [refs, position] = usePosition({ placement, visible });

  // Close if clicked outside of the dropdown target or attachment
  useClickOutside([refs.target, refs.attachment], visible && toggleVisible);

  useEscape(visible && closeOnEscape && toggleVisible);

  useEffect(() => {
    setIsVisible(!disabled && open);
  }, [disabled, open]);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    if (visible) {
      onShow();
    } else {
      onHide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const render = element => {
    // If the provided element is a render function, invoke the function
    if (typeof element === 'function') {
      return element({ onClose: toggleVisible, visible });
    }

    // Assert that a single element is provided e.g. no fragments
    const child = Children.only(element);

    // Clone the element and pass the ref to be used for positioning
    return cloneElement(child, { onClose: toggleVisible, visible });
  };

  return (
    <>
      <Target
        {...props}
        aria-disabled={disabled}
        ref={refs.target}
        onClick={toggleVisible}
      >
        {render(children)}
      </Target>

      <Portal>
        {visible ? (
          <Attachment ref={refs.attachment} style={position}>
            {render(attachment)}
          </Attachment>
        ) : null}
      </Portal>
    </>
  );
}

Dropdown.propTypes = {
  // Component that will be attached/tethered to the target
  attachment: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

  // Target component to tether attachment
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

  // Whether to bind Escape to close dropdown
  closeOnEscape: PropTypes.bool,

  // Whether the dropdown should be disabled
  disabled: PropTypes.bool,

  // Attachment placement
  placement: PropTypes.oneOf(['left', 'right']),

  // Whether or not the dropdown should initialize visible
  open: PropTypes.bool,

  // Fires when the attachment is shown
  onShow: PropTypes.func,

  // Fires when the attachment is hidden
  onHide: PropTypes.func,
};

export default styled(Dropdown)``;

/**
 * MenuList
 */

export const List = styled.ul.attrs({ role: 'listbox' })`
  list-style: none;
`;

export const Item = styled.li.attrs({ role: 'option' })`
  align-items: center;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  font-size: var(--regular);
  padding: 8px 12px;
  white-space: nowrap;

  :hover {
    background: var(--dropdown-option-bg);
  }

  ${Link} {
    font-weight: var(--normal);
    color: inherit;
    :hover {
      color: inherit;
    }
  }

  ${({ isSelected }) =>
    isSelected &&
    `
      color: var(--dropdown-option-selected);
      font-weight: var(--bold);
    `}

  > ${Icon} {
    font-size: inherit;
    margin-right: 8px;
    width: 20px;
  }
`;

function BaseMenuList({ options, onClose, ...props }) {
  return (
    <List {...props} onClick={onClose}>
      {options.map(({ icon, isSelected, label, ...rest }) => (
        <Item
          key={label}
          aria-selected={isSelected}
          isSelected={isSelected}
          {...rest}
        >
          {icon && <Icon icon={icon} />}
          {label}
        </Item>
      ))}
    </List>
  );
}

BaseMenuList.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      isSelected: PropTypes.bool,
      label: PropTypes.node,
    })
  ),
  onClose: PropTypes.func,
};

export const MenuList = styled(BaseMenuList)``;

/**
 * SortDropdown
 */

const Wrapper = styled.div`
  align-items: center;
  color: var(--dropdown-text);
  display: flex;

  > ${Icon} {
    margin-right: 8px;
  }
`;

function BaseSortDropdown({ onChange, options, value, ...props }) {
  const items = useMemo(() => {
    return options.map(option => ({
      ...option,
      isSelected: value === option.value,
      onClick: event => onChange(option, event),
    }));
  }, [onChange, options, value]);

  const label = useMemo(() => {
    const selected = options.find(option => value === option.value);
    return selected?.label || 'Sort by...';
  }, [options, value]);

  return (
    <Dropdown {...props} attachment={<MenuList options={items} />}>
      <Wrapper>
        <Icon icon="sort" />
        {label}
      </Wrapper>
    </Dropdown>
  );
}

BaseSortDropdown.propTypes = {
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  value: PropTypes.string,
};

export const SortDropdown = styled(BaseSortDropdown)``;
